import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Search, XCircle, CalendarDays, CalendarRange, Loader2, TrendingUp, Users } from 'lucide-react';
import api from '../../services/api';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import { DateRangeFilter, PRESETS, toISODate } from '../../components/admin/search/DateRangeFilter';
import { DailySearchChart, fmtDay } from '../../components/admin/search/DailySearchChart';
import { TopTermsTable } from '../../components/admin/search/TopTermsTable';
import { TermDetailSheet } from '../../components/admin/search/TermDetailSheet';
import { TypewriterConfigCard } from '../../components/admin/search/TypewriterConfigCard';

const TONES = {
  orange: ['bg-orange-500/15', 'text-orange-400'], blue: ['bg-blue-500/15', 'text-blue-400'],
  green: ['bg-emerald-500/15', 'text-emerald-400'], red: ['bg-rose-500/15', 'text-rose-400'], sky: ['bg-sky-500/15', 'text-sky-400'],
};

const StatCard = ({ icon: Icon, label, value, sub, tone = 'orange', testid }) => (
  <Card className="bg-gray-800 border-gray-700 p-5" data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${TONES[tone][0]}`}>
        <Icon className={`w-5 h-5 ${TONES[tone][1]}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-400 truncate">{label}</p>
        <p className="text-2xl font-black text-white tabular-nums" data-testid={`${testid}-value`}>{value}</p>
      </div>
    </div>
    {sub && <p className="text-xs text-gray-500 mt-2">{sub}</p>}
  </Card>
);

const AdminSearchEngine = () => {
  const [range, setRange] = useState(() => PRESETS[2].range());
  const [preset, setPreset] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTerm, setSelectedTerm] = useState(null);

  const isoRange = useMemo(() => ({ start: toISODate(range.from), end: toISODate(range.to) }), [range]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/analytics/searches', {
        params: isoRange, headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      setAnalytics(data);
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Error cargando analítica', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [isoRange]);

  useEffect(() => { load(); }, [load]);

  const r = analytics?.range;
  const t = analytics?.totals;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-search-engine">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Search className="w-6 h-6 text-orange-400" /> Motor de Búsqueda</h1>
            <p className="text-gray-400 text-sm mt-1">Analítica de lo que buscan tus clientes (hora UTC)</p>
          </div>
          <DateRangeFilter value={range} preset={preset} onChange={(rg, p) => { setRange(rg); setPreset(p); }} />
        </div>

        {!analytics ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard icon={CalendarDays} label="Búsquedas hoy" value={t.today.toLocaleString('es-MX')} tone="orange" testid="stat-searches-today" />
              <StatCard icon={CalendarRange} label="Búsquedas este mes" value={t.month.toLocaleString('es-MX')} sub={`${t.year.toLocaleString('es-MX')} en el año`} tone="blue" testid="stat-searches-month" />
              <StatCard icon={Search} label="Total histórico" value={t.all_time.toLocaleString('es-MX')} tone="green" testid="stat-searches-total" />
              <StatCard icon={TrendingUp} label="En el periodo" value={r.total.toLocaleString('es-MX')} sub={`${r.unique_users} usuarios identificados`} tone="sky" testid="stat-searches-range" />
              <StatCard icon={XCircle} label="Sin resultados (periodo)" value={`${r.zero_results_pct}%`} sub={`${r.zero_results} búsquedas fallidas`} tone="red" testid="stat-zero-results" />
            </div>

            <Card className={`bg-gray-800 border-gray-700 p-6 transition-opacity ${loading ? 'opacity-60' : ''}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2"><CalendarDays className="w-5 h-5 text-orange-400" /> Búsquedas por día</h2>
                <p className="text-xs text-gray-500" data-testid="search-range-label">
                  {fmtDay(r.start, { day: 'numeric', month: 'short', year: 'numeric' })} – {fmtDay(r.end, { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
              <DailySearchChart data={analytics.by_day} />
            </Card>

            <Card className={`bg-gray-800 border-gray-700 p-6 transition-opacity ${loading ? 'opacity-60' : ''}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h2 className="text-lg font-bold text-white flex items-center gap-2"><Users className="w-5 h-5 text-orange-400" /> Términos más buscados</h2>
                <p className="text-xs text-gray-500">Haz clic en un término para ver su detalle</p>
              </div>
              <TopTermsTable terms={analytics.top_terms} onSelect={setSelectedTerm} />
            </Card>
          </>
        )}

        <TypewriterConfigCard />
        <TermDetailSheet term={selectedTerm} range={isoRange} onClose={() => setSelectedTerm(null)} />
      </div>
    </AdminLayout>
  );
};

export default AdminSearchEngine;
